import { Box, Stack, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useParams } from "react-router";

import { Answer, FileUpload, Question as TQuestion, QuestionType } from "../../globals/types";
import { useRefetchAssessment } from "../../hooks/useAssessmentDetail";
import useToken from "../../hooks/useToken";
import { isClosed, saveAnswer } from "../../lib/formsApi";
import { hasStandardDescription, LocalizedContent, StandardDescription } from "../../lib/i18n/contentTranslations";
import { Files } from "../Files";
import { initialValue } from "../Form/helpers";

import { AttachedFiles } from "./AttachedFiles";
import BooleanQuestion from "./BooleanQuestion";
import ClearAnswer from "./ClearAnswer";
import DateQuestion from "./DateQuestion";
import { QuestionProps } from "./interfaces";
import MultiSelectQuestion from "./MultiSelectQuestion";
import NumberQuestion from "./NumberQuestion";
import RatingQuestion from "./RatingQuestion";
import RatingWithCommentQuestion from "./RatingWithCommentQuestion";
import SingleSelectQuestion from "./SingleSelectQuestion";
import TextQuestion from "./TextQuestion";

const ID_PREFIX = "question-";

type PreviewProps = { preview: true } | { preview?: false; assessmentId?: string };

type Props = {
    question: TQuestion;
    answer?: Answer;
    number: number;
    disabled?: boolean;
    setSubmitBlocked: React.Dispatch<React.SetStateAction<boolean>>;
    "data-testid"?: string;
} & PreviewProps;

const QuestionBody: React.FC<
    Pick<Props, "question" | "answer" | "disabled" | "data-testid"> & Pick<QuestionProps, "saveDraft" | "name">
> = ({ question, answer, disabled, saveDraft, name, "data-testid": dataTestId }) => {
    const questionProps = {
        name,
        question,
        answer,
        saveDraft,
        disabled,
        "data-testid": dataTestId,
        "aria-labelledby": `${name}-title`,
        "aria-describedby": `${name}-description`,
    };

    switch (question.type) {
        case QuestionType.Text:
            return <TextQuestion {...questionProps} />;
        case QuestionType.Boolean:
            return <BooleanQuestion {...questionProps} />;
        case QuestionType.Date:
            return <DateQuestion {...questionProps} />;
        case QuestionType.Number:
            return <NumberQuestion {...questionProps} />;
        case QuestionType.SingleSelect:
            return <SingleSelectQuestion {...questionProps} />;
        case QuestionType.MultiSelect:
            return <MultiSelectQuestion {...questionProps} />;
        case QuestionType.Rating:
            return <RatingQuestion {...questionProps} />;
        case QuestionType.RatingWithComment:
            return <RatingWithCommentQuestion {...questionProps} />;
        default:
            return <TextQuestion {...questionProps} />;
    }
};

const Question: React.FC<Props> = ({ number, ...props }) => {
    const { getFieldHelpers } = useFormikContext<Record<string, initialValue>>();
    const { setTouched: setFileNamesTouched, setValue: setFileNames } = getFieldHelpers<string[]>(
        `${props.question.id}.fileNames`
    );
    const { setTouched: setValueTouched } = getFieldHelpers<string[]>(`${props.question.id}.value`);
    const { id } = useParams<{ id: string }>();

    // Further destructuring here in order to pass props to QuestionBody.
    const { question, answer, setSubmitBlocked } = props;
    const name = `${question.id}.value`;
    const dataTestId = `${ID_PREFIX}${question.type}-${question.id}`;

    const { token } = useToken();
    const refetchAssessment = useRefetchAssessment();
    const [error, setError] = useState(false);

    /** Save draft answer and upload files. */
    const saveDraft: QuestionProps["saveDraft"] = async (value, fileNames, comment) => {
        if (props.preview) return "";
        try {
            const body = {
                value: value ?? answer?.value,
                fileNames: fileNames ?? answer?.fileNames,
                comment: comment ?? answer?.comment ?? undefined,
            };
            const saved = await saveAnswer(token, id ?? "", question.id, body);
            const answerId = saved.id;
            if (fileNames !== undefined || answer === undefined) setFileNames(saved.fileNames);
            setError(false);
            await refetchAssessment();
            if (value) setValueTouched(true);
            if (fileNames) setFileNamesTouched(true);
            return answerId;
        } catch (err) {
            if (isClosed(err)) {
                await refetchAssessment();
            } else {
                setError(true);
            }
            throw err;
        }
    };

    return (
        <Stack width="100%" spacing={2} sx={{ position: "relative" }}>
            {!props.disabled && (
                <Box sx={{ position: "absolute", top: 0, right: 0 }}>
                    <ClearAnswer question={question} saveDraft={saveDraft} />
                </Box>
            )}
            <Stack spacing={1}>
                <Stack direction="row" spacing={2} alignItems="baseline" sx={{ pr: 12 }}>
                    <Typography variant="textMd" fontWeight={500}>
                        {/* eslint-disable-next-line react/jsx-no-literals */}
                        {number}.{" "}
                        <LocalizedContent translationKey={question.titleTranslationKey} fallback={question.title} />
                    </Typography>
                    {question.required && (
                        <Typography
                            variant="textXs"
                            color="textTextHelper"
                            sx={{ textTransform: "uppercase", letterSpacing: "0.04em", flexShrink: 0 }}
                        >
                            <FormattedMessage defaultMessage="Required" />
                        </Typography>
                    )}
                    {error && (
                        <Typography variant="textSm" color="textTextError">
                            <FormattedMessage defaultMessage="Error uploading answer. Please clear and retry" />
                        </Typography>
                    )}
                </Stack>
                {question.description ? (
                    <Typography
                        sx={{ whiteSpace: "pre-line" }}
                        variant="textSm"
                        fontWeight={400}
                        color="textTextHelper"
                    >
                        <LocalizedContent
                            translationKey={question.descriptionTranslationKey}
                            fallback={question.description}
                        />
                    </Typography>
                ) : (
                    hasStandardDescription(question.titleTranslationKey) && (
                        <Typography variant="textSm" fontWeight={400} color="textTextHelper">
                            <StandardDescription translationKey={question.titleTranslationKey} />
                        </Typography>
                    )
                )}
            </Stack>
            {question.attachedFileNames.length > 0 && (
                <Stack spacing={1}>
                    <Typography variant="textSm">
                        <FormattedMessage defaultMessage="Attachments" />
                    </Typography>
                    <Stack direction="row">
                        <AttachedFiles fileNames={question.attachedFileNames} questionId={question.id} />
                    </Stack>
                </Stack>
            )}
            <Stack direction="row">
                <QuestionBody
                    saveDraft={saveDraft}
                    {...props}
                    name={name}
                    data-testid={dataTestId}
                    disabled={props.disabled}
                />
            </Stack>
            {question.fileUpload !== FileUpload.Disabled && (
                <Stack direction="row">
                    <Files
                        answer={answer}
                        saveDraft={saveDraft}
                        questionId={question.id}
                        uploadType={question.fileUpload}
                        disabled={props.disabled}
                        setSubmitBlocked={setSubmitBlocked}
                    />
                </Stack>
            )}
        </Stack>
    );
};

export default Question;
